import { BaseDialog } from '../../common/dialogs/BaseDialog';
import { useSelector } from 'react-redux';
import {
  selectSelectedPrompt,
  selectSelectedPromptGroup,
  selectIsLoading,
} from '../../../reducers/promptLibraryReducer';
import { PromptGroupsView } from './dialogViews/PromptGroupsView';
import { EditPromptView } from './dialogViews/EditPromptView';
import { PromptsView } from './dialogViews/PromptsView';
import { useEffect } from 'react';
import { usePromptGroups } from './usePromptGroups';
import { CenteredCircularProgress } from '../../common/CenteredCircularProgress';
import useInitializePromptGroups from './useInitializePromptGroups';
import { ArtifactProvider } from '../../common/artifacts/useArtifacts';

interface PromptsLibraryDialogProps {
  open: boolean;
  onClose: () => void;
  onPromptClick: (promptText: string) => Promise<void>;
}

export const PromptsLibraryDialog = ({
  open,
  onClose,
  onPromptClick,
}: PromptsLibraryDialogProps) => {
  useInitializePromptGroups();
  const { resetState } = usePromptGroups();
  const selectedPromptGroup = useSelector(selectSelectedPromptGroup);
  const selectedPrompt = useSelector(selectSelectedPrompt);
  const isLoading = useSelector(selectIsLoading);

  const shouldRenderPromptGroupsView = !selectedPrompt && !selectedPromptGroup && !isLoading;
  const shouldRenderSinglePromptView = !selectedPrompt && selectedPromptGroup && !isLoading;
  const shouldRenderEditPromptView = selectedPromptGroup && selectedPrompt && !isLoading;

  useEffect(() => {
    return () => {
      resetState();
    };
  }, [resetState]);

  return (
    <ArtifactProvider>
      <BaseDialog
        open={open}
        disableBackdropClickClose={!!shouldRenderEditPromptView}
        onClose={onClose}
        sx={{
          '& .MuiDialog-paper': {
            maxHeight: 'calc(100% - 164px)',
            height: '100%',
            width: '100%',
            maxWidth: 'calc(100% - 164px)',
          },
        }}
      >
        {isLoading && <CenteredCircularProgress />}
        {shouldRenderPromptGroupsView && <PromptGroupsView onClose={onClose} />}
        {shouldRenderSinglePromptView && (
          <PromptsView onClose={onClose} onPromptClick={onPromptClick} />
        )}
        {shouldRenderEditPromptView && <EditPromptView />}
      </BaseDialog>
    </ArtifactProvider>
  );
};
